import type maplibregl from "maplibre-gl";
import { RasterTileSource } from "maplibre-gl";

// types.ts
export namespace MapLibre {
  export type BackgroundLayer = maplibregl.BackgroundLayerSpecification;
  export type FillLayer = maplibregl.FillLayerSpecification;
  export type LineLayer = maplibregl.LineLayerSpecification;
  export type SymbolLayer = maplibregl.SymbolLayerSpecification;
  export type RasterLayer = maplibregl.RasterLayerSpecification;
  export type CircleLayer = maplibregl.CircleLayerSpecification;
  export type FillExtrusionLayer = maplibregl.FillExtrusionLayerSpecification;
  export type HeatmapLayer = maplibregl.HeatmapLayerSpecification;
  export type HillshadeLayer = maplibregl.HillshadeLayerSpecification;
}
type InlineSource = {
  type: "geojson";
  data: any;
  buffer?: number;
  tolerance?: number;
  cluster?: boolean;
  clusterMaxZoom?: number;
  clusterRadius?: number;
  maxzoom?: number;
};

/**
 * App-level properties attached to dynamic CM Table layer specs.
 * These are not part of the base MapLibre type specs.
 */
type CmLayerExtras = {
  initialVisibility?: boolean;
  disableClicking?: boolean;
};

export type ExtendedFillLayer = Omit<MapLibre.FillLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type ExtendedLineLayer = Omit<MapLibre.LineLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type ExtendedSymbolLayer = Omit<MapLibre.SymbolLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type ExtendedRasterLayer = Omit<MapLibre.RasterLayer, "source"> &
  CmLayerExtras & {
    source: string | RasterTileSource;
  };

/** Extended circle layer — used by CM Table point features */
export type ExtendedCircleLayer = Omit<MapLibre.CircleLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type MapLibreLayerSpecification =
  | ExtendedFillLayer
  | ExtendedLineLayer
  | ExtendedSymbolLayer
  | ExtendedRasterLayer
  | ExtendedCircleLayer;

interface BaseLayerConfig {
  id: string;
  type: string;
  url: string;
  /** Display name in the legend / layers panel. */
  name?: string;
  useBasicAuth?: boolean;
  useOAuth?: boolean;
  initialVisibility: boolean;
  disableClicking?: boolean;
  disablePopups?: boolean;
  showFiltersOnClick?: boolean;
  searchable?: boolean;
  searchableFields?: string[];
  queryParams?: { [key: string]: string };
  joinedData?: {
    name: string;
    url: string;
    templateFields: {
      parentField: string;
      parentTargetTable?: string;
    };
    enableFilters?: boolean;
    filters?: {
      [key: string]: string;
    };
  }[];
  entityMapping?: {
    table: string;
  };
  error?: boolean;
}

// Search result interface for the map search functionality
export interface SearchResult {
  id: string | number;
  name: string;
  type: string; // 'coordinate', 'place', etc. - matches layer ID
  coordinates?: [number, number]; // [lng, lat]
  properties?: Record<string, any>;
  layerId?: string; // Reference to the layer it belongs to
}

export interface RasterDemLayerConfig extends BaseLayerConfig {
  type: "raster-dem";
  options: {
    minZoom: number;
    maxZoom: number;
    tileSize: number;
    exaggeration: number;
  };
}

export interface GeoJsonLayerConfig extends BaseLayerConfig {
  type: "geojson";
  metadata?: {
    isFulcrum?: boolean;
  };
  geometry: "circle" | "line" | "fill" | "symbol"; // Specifies how the GeoJSON data should be rendered
  options?: {
    minZoom?: number;
    maxZoom?: number;
  };
  defaultIcon: string; // Only required if geometry === "symbol"
  /** A MapLibre filter limiting the layer to one geometry family (see `layerStyle.geometryFamilyFilter`). */
  geometryFilter?: unknown[];
  styleByField?: string; // Field to use to apply colour ramps
  filters?: {
    type: string;
    db_table: string;
    field: string;
  }[];
  icons?: string[]; // Only required if geometry === "symbol"
  popupFields?: string[]; // Fields to show in popup
  descriptionField?: string; // Field to use for description
  /**
   * GeoStyler style (the stored, renderer-neutral styling source of truth). When
   * present the map render path uses it exclusively via `geostylerToMapLibreLayers`;
   * the legacy `style`/`defaultIcon` shorthand below is ignored for rendering and
   * kept only for the legend/layers panel until it is migrated too.
   *
   * Typed shallowly (not the full `geostyler-style` `Style`) on purpose: that type
   * recurses through `Filter`, which explodes immer's draft depth in the layer store
   * (TS2589). Consumers cast to the real `Style` at the converter boundary.
   */
  geostyler?: { name?: string; rules: unknown[] };
  style: {
    // Specific to line type layers
    "line-color"?: string;
    "line-width"?: number;
    "line-opacity"?: number;
    "line-dasharray"?: number[];
    // Specific to fill type layers (a plain fill uses `style` as its paint)
    "fill-color"?: string;
    "fill-opacity"?: number;
    "fill-outline-color"?: string;
    /** Outline width in px; above 1 the outline is drawn as a line layer (see `layerStyle.areaPaint`). */
    outlineWidth?: number;
    // Specific to circle type layers
    "circle-radius"?: number;
    "circle-color"?: string;
    "circle-stroke-width"?: number;
    "circle-stroke-color"?: string;
    // For labels
    labelField?: string;
    // Label properties can now be either static values or zoom-dependent expressions
    // e.g., ["step", ["zoom"], 0, 10, 12, 14, 16] - invisible at zoom < 10, size 12 at zoom 10-14, size 16 at zoom > 14
    labelSize?: number | any[];
    labelColor?: string | any[];
    labelFont?: string[] | any[];
    labelOffset?: [number, number] | any[];
    labelOpacity?: number | any[]; // Control label opacity based on zoom
    labelHaloColor?: string | any[]; // Text outline color
    labelHaloWidth?: number | any[]; // Text outline width
    iconSize?: number; // Only required if geometry === "symbol
    iconStyle?: any;
    opacity?: number;
  };
}

export interface RasterLayerConfig extends BaseLayerConfig {
  type: "raster";
  options: {
    tileSize: number;
    opacity: number;
  };
  /**
   * Optional override for the WMS `GetLegendGraphic` URL rendered by the legend
   * overlay. Use this when the server's default legend (20x20 cells built from the
   * layer name) is wrong for the specific sub-layer — e.g. raster ramps whose
   * GetCapabilities `<LegendURL>` advertises a native size like 38x595. When
   * present, the overlay uses this URL verbatim instead of composing one from
   * `queryParams`.
   */
  legendUrl?: string;
}

// After RasterLayerConfig, add a new interface for Fulcrum layers
export interface FulcrumLayerConfig extends Omit<GeoJsonLayerConfig, "type"> {
  type: "fulcrum";
  formId: string;
  region?: "US" | "AU" | "EU";
}

// Retained for backward compatibility — config.json now uses type:"geojson" with the /api/v2/media/geojson/ endpoint (Path A).
export interface MediaGeotaggedLayerConfig extends Omit<GeoJsonLayerConfig, "type"> {
  type: "media-geotagged";
}

export interface CogLayerConfig extends BaseLayerConfig {
  type: "cog";
  bounds?: [[number, number], [number, number]];
  /**
   * Static raster paint computed from the dataset's GeoStyler `Raster`
   * symbolizer — hue-rotate/brightness-min/brightness-max/saturation/contrast/
   * resampling/fade-duration (Tier 1, see `geostylerToMapLibre.ts`). Deliberately
   * excludes `raster-opacity`, which stays on `options.opacity` — the one raster
   * paint property with an existing live-adjustable control
   * (`useLayerActions().setLayerOpacity`).
   */
  rasterPaint?: Record<string, unknown>;
  options?: {
    minZoom?: number;
    maxZoom?: number;
    tileSize?: number;
    opacity?: number;
  };
}

export interface PMTilesLayerConfig extends BaseLayerConfig {
  type: "pmtiles";
  layerFormat?: "vector" | "raster";
  sourceLayer?: string;
  style?: any;
  bounds?: [[number, number], [number, number]];
  /** Raster PMTiles only — see {@link CogLayerConfig.rasterPaint}. */
  rasterPaint?: Record<string, unknown>;
  options?: {
    minZoom?: number;
    maxZoom?: number;
    tileSize?: number;
    opacity?: number;
  };
}

// Update the MapLayerConfig type to include FulcrumLayerConfig
export type MapLayerConfig =
  | RasterDemLayerConfig
  | GeoJsonLayerConfig
  | RasterLayerConfig
  | FulcrumLayerConfig
  | MediaGeotaggedLayerConfig
  | PMTilesLayerConfig
  | CogLayerConfig;

// Update LegendLayerConfig to include FulcrumLayerConfig too
export type LegendLayerConfig =
  | GeoJsonLayerConfig
  | RasterLayerConfig
  | FulcrumLayerConfig
  | MediaGeotaggedLayerConfig
  | PMTilesLayerConfig
  | CogLayerConfig;

/**
 * Ordered candidate keys used to resolve display fields on an endpoint.
 *
 * For Fulcrum, list **GeoJSON `data_name`s** (snake_case) **and** the human
 * form labels. Do not rely on 4-character Fulcrum field IDs alone — bulk GeoJSON
 * and the data-grid matcher look up labels / snake_case, so IDs like `"cc60"`
 * produce "Fulcrum Record …" titles. First non-empty match wins.
 *
 * Card / list / timeline titles use `apiEndpoints[].dataSourceConfig.fieldMapping`
 * (V2: `externalDataSources[]`). Map pin labels use `mapLayers[].style.labelField`,
 * which must be a GeoJSON property, e.g. `"{job_category}"`, not `"{name}"`.
 *
 * @see README.md — "Fulcrum display names and descriptions"
 */
export interface FieldMapping {
  id: string | string[]; // Field path(s) for ID
  /** Ordered candidates for card, list, and timeline title. */
  name: string | string[];
  /** Ordered candidates for card/list subtitle and popup body. */
  description?: string | string[];
  thumbnail?: string | string[];
  created_at?: string | string[];
  last_updated_at?: string | string[];
  url?: string | string[];
  media_type?: string | string[];
  geometry?: string | string[];
  coordinates?: string | string[];

  // Custom transformation functions
  transformers?: {
    [field: string]: (value: any, originalData: any) => any;
  };
}

export interface StandardizedDataItem extends MediaAssetFields {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string | null;

  // Optional common fields
  thumbnail?: string;
  created_at?: string;
  last_updated_at?: string;

  // Primary image (for entities with photos)
  primary_image?: {
    id: string | number;
    url: string;
    thumbnail: string;
    name?: string;
    description?: string;
  };

  // Media-specific
  url?: string;
  media_type?: string;
  original_file?: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };

  // Geographic
  geometry?: string | object;
  coordinates?: [number, number];

  // Relationships (standardized format)
  relationships?: RelationshipsResponse;

  // Normalized relations
  relations?: INormalisedRelations;

  // Plain `restrictions: [{id, name}]` field on CM table rows / media items —
  // not part of `relationships`/`relations`, PATCHed directly as `restrictions: number[]`.
  restrictions?: { id: number | string; name: string }[];

  // Fulcrum-specific data (optional)
  fulcrum_data?: {
    form_id: string;
    created_by?: string;
    updated_by?: string;
    form_values: Record<string, any>;
    photos?: any[];
    mapped_fields: Record<string, any>;
    // Store full media objects here for MediaDrawer to access
    fullMediaObjects?: any[];
  };

  // Source metadata for debugging
  _source: {
    type: "django" | "fulcrum" | "external";
    originalId: string | number;
    rawData: any;
  };
}

// Adapter function signature - now async for relationship resolution
export type DataSourceAdapter = (
  rawData: any,
  fieldMapping?: FieldMapping,
  apiEndpoints?: ApiEndpoint[]
) => Promise<StandardizedDataItem[]>;

export interface ICmTableFieldDef {
  name: string;
  type: "string" | "number" | "boolean" | "integer";
  format?: "textarea" | "uri" | "date" | "date-time" | "email";
  label?: string;
  required?: boolean;
  enum?: (string | number)[];
  description?: string;
}

export interface ApiEndpoint {
  name: string;
  tableName: string;
  queryParam?: string;
  api_endpoint: string;
  description: string;
  entityType?: "geo" | "media" | "related" | "spatial-dataset" | "table";
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  formId?: string; // Fulcrum form ID for Fulcrum data sources
  mapLayerId?: string; // ID of the corresponding map layer (if different from tableName)
  tableSlug?: string; // Explicit slug for CM Table endpoints (prevents overloading tableName)

  // New optional fields for data source adapter pattern (100% backwards compatible)
  dataSourceType?: "django" | "fulcrum" | "external" | "cm-table"; // defaults to 'django' if not specified
  isEditable?: boolean; // defaults to true for django, false for external sources
  dataSourceConfig?: {
    fieldMapping?: FieldMapping;
    customTransformers?: Record<string, (value: any, originalData: any) => any>;
  };
  geometryType?: "Point" | "Polygon" | "LineString"; // Type of geometry this endpoint handles
  requiresGeometry?: boolean; // Whether geometry is required for creating new items
  reverseRelationships?: {
    sourceFormId: string;
    sourceFormName: string;
    recordLinkFieldId: string;
    recordLinkFieldLabel: string;
    relationshipKey: string;
    displayName: string;
  }[]; // Configuration for reverse relationships (records that link TO this endpoint)

  // Date configuration for timeline/calendar view support
  dateConfig?: {
    hasDateFilter: boolean;
    dateField?: string; // Single date field
    startDateField?: string; // Start date field for date ranges
    endDateField?: string; // End date field for date ranges
    showCalendarByDefault?: boolean; // Auto-show timeline when this endpoint is selected
  };

  /**
   * The published CmForm bound to this CM table, as served in the config. Present
   * only when the table was created from (or later bound to) a form. Knowing this
   * synchronously lets the entity form lay itself out for form-driven capture
   * without flashing its generic Name/Description inputs while the schema loads —
   * the schema itself still comes from `field/tables/<slug>/forms/`.
   */
  form?: {
    formGroupId: string;
    uuid: string;
    version: number;
    status: string;
    name: string;
    hasUnpublishedDraft?: boolean;
  };

  // CM Table Schema Fields
  // `"geometry"` = the table accepts any kind (a form with every geometry type
  // enabled); the draw map already treats it as multi-capable.
  spatialType?: "point" | "multipoint" | "polygon" | "multipolygon" | "linestring" | "multilinestring" | "geometry" | "none";
  style?: Record<string, any>; // MapLibre paint overrides (e.g. "line-color", "line-width")
  styleKey?: string; // Row `map_display_config` key the styling value is stored under (GeoStyler `style_key`)
  fields?: ICmTableFieldDef[]; // Schema definition for row_data fields
}

export interface Dashboard {
  name: string;
  url: string;
}

interface MediaUploadConfig {
  service?: "imagekit" | "s3";
}

export interface Config {
  /** Present only on the database-driven config from `GET /api/v2/config/`; absent for legacy static config. */
  schema_version?: string;
  appTitle: string;
  logoPath: string;
  loginImagePath: string;
  loginMessage: string;
  colorScheme: {
    primary: string;
    secondary: string;
  };
  sidebarOpen: boolean;
  primaryFilterLayer: string;
  mapLayers: MapLayerConfig[];
  apiEndpoints: ApiEndpoint[];
  homePageShortcuts: Array<{
    name: string;
    targetTab: number;
    path?: string;
    options: { tableName?: string };
  }>;
  homePageTitle: string;
  homePageDescription: string;
  initialMapView: {
    longitude: number;
    latitude: number;
    zoom: number;
    pitch: number;
  };
  dashboardUrl?: string; // Keep for backward compatibility
  dashboardUrls?: Dashboard[]; // Keep for backward compatibility
  toolsUrls?: Dashboard[]; // New preferred naming for external tools
  defaultDataTable?: string;
  showMediaInRelatedItemsCloud?: boolean;
  showGeoInRelatedItemsCloud?: boolean;
  showIndirectRelationshipsInRing?: boolean;
  terrainExaggeration?: number; // Added for 3D terrain control
  backgroundOpacity?: number; // Controls page background overlay opacity (0.0 to 1.0)
  mediaUpload?: MediaUploadConfig;
  enableFulcrumEditing?: boolean; // Global flag to enable editing of Fulcrum data
  hideCardsWithoutImages?: boolean; // Hide cards that don't have primary images in DataGrid
  limitDataDrawerToExtent?: boolean; // When true, data drawer sorts visible-on-map items first and greys out the rest
  alwaysRequestRelatedItemsAndMedia?: boolean; // Always load Level 3 (relationships & media) instead of progressive loading
  /** When true, map camera may fit bounds to all highlighted related items; when false/omitted, only the selected entity geometry is used. */
  enableZoomToRelatedItems?: boolean;
  enableServerSearch?: boolean; // Enable/disable server search functionality
  autoExpandAdditionalDetailsWhenNoImage?: boolean; // Auto-expand Additional Details when items have no primary image or thumbnail
  auth?: {
    useCloudFront: boolean;
    endpoint: string;
    cloudFrontEndpoint: string;
  };
  // Dashboards tab configuration
  dashboardsTab?: {
    enabled?: boolean; // Default true for backward compatibility
    label?: string; // Default "Tools"
  };
  tiltMapOnPointItemSelect?: boolean;
  /**
   * Tabular (CSV → Parquet / Power BI) datasets panel config from `GET /api/v2/config/`.
   * When present, a dedicated "Tabular data" tab is rendered that lists SpatialDatasets
   * with `dataset_type='tabular'` (never on the map). See TABULAR_DATASET_FRONTEND.md.
   */
  tabularData?: {
    name: string;
    /** e.g. "/api/v2/spatial-datasets/?dataset_type=tabular" */
    listEndpoint: string;
    /** e.g. "/api/v2/powerbi/csv/" — consumed as `${powerbiPull}?dataset_id=<id>` */
    powerbiPull: string;
    /**
     * e.g. "/api/v2/csv-data/" — paginated CSV-as-JSON rows, consumed as
     * `${csvData}?file_key=<original_file_key>&limit=100&offset=0`. Uses the normal
     * CultureMap login Bearer token (NOT a data-access token). Optional; defaults to
     * "/api/v2/csv-data/" when the backend config omits it.
     */
    csvData?: string;
  };
}

// Base interface for all items
export interface BaseDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string;
  thumbnail?: string;
  created_at?: string;
  last_updated_at?: string;
  relationships?: RelationshipsResponse;
  relations?: INormalisedRelations;
  geometry?: any;
}

// Base interface for URLs that might be presigned
interface PresignedUrlInfo {
  presigned?: boolean;
  presigned_expiry?: number; // Unix timestamp
}

// Media specific fields
export interface MediaItem extends BaseDataItem, PresignedUrlInfo, MediaAssetFields {
  url: string;
  thumbnail: string;
  media_type: string;
  is_primary?: boolean;

  // File metadata
  original_file: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };
}

// Primary image interface used by other entities
export interface PrimaryImage extends PresignedUrlInfo {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  url: string | null;
  thumbnail: string | null;
  name?: string;
  description?: string;
}

// Specific types (exported)
export interface EntityItem extends BaseDataItem {
  primary_image?: {
    url?: string | null;
    thumbnail?: string | null;
  };
}

export interface GeoItem extends EntityItem {
  geometry: string;
}

// The main union type for use in components
export type FlexibleDataItem = EntityItem | MediaItem | GeoItem;

export interface HomePageShortcut {
  name: string;
  targetTab: number;
  path?: string;
}

export interface Option {
  id: number;
  name: string;
  description?: string;
  thumbnail?: string;
  is_primary?: boolean;
}

export interface GeoJSONPosition {
  0: number;
  1: number;
  2?: number;
}

export interface GeoJSONPoint {
  type: "Point";
  coordinates: GeoJSONPosition;
}

export interface GeoJSONMultiPoint {
  type: "MultiPoint";
  coordinates: GeoJSONPosition[];
}

export interface GeoJSONLineString {
  type: "LineString";
  coordinates: GeoJSONPosition[];
}

export interface GeoJSONMultiLineString {
  type: "MultiLineString";
  coordinates: GeoJSONPosition[][];
}

export interface GeoJSONPolygon {
  type: "Polygon";
  coordinates: GeoJSONPosition[][];
}

export interface GeoJSONMultiPolygon {
  type: "MultiPolygon";
  coordinates: GeoJSONPosition[][][];
}

export interface GeoJSONGeometryCollection {
  type: "GeometryCollection";
  geometries: GeoJSONGeometry[];
}

export type GeoJSONGeometry =
  | GeoJSONPoint
  | GeoJSONMultiPoint
  | GeoJSONLineString
  | GeoJSONMultiLineString
  | GeoJSONPolygon
  | GeoJSONMultiPolygon
  | GeoJSONGeometryCollection;

export interface GeoJSONFeature {
  type: "Feature";
  geometry: GeoJSONGeometry;
  properties: {
    name: string;
    [key: string]: any;
  };
}

export interface PreviewMapData {
  areas: GeoJSONFeature[];
  points: GeoJSONFeature[];
}

export type FileProgressStage = "pending" | "hashing" | "presigning" | "uploading" | "processing" | "server" | "completed" | "failed";

export interface FileProgress {
  stage: FileProgressStage;
  completed: number;
  total: number;
  progress: number;
}

export interface UploadProgressBarsProps {
  progress: Record<string, FileProgress>;
  items: MediaItemDetailed[];
  onComplete?: () => void;
  onFailure?: () => void;
  onCancel?: () => void;
  /** Retries the failed files; shown with Close once the batch has settled with failures. */
  onRetry?: () => void;
  isFinish?: boolean;
  isFailed?: boolean;
  isRetrying?: boolean;
  /** Why each failed file failed, by item id. */
  failures?: Record<string, string>;
}

export interface RelatedEntityItem extends MediaAssetFields {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name?: string; // For server inconsistency
  description?: string; // for server inconsistency
  rel_description?: string;
  is_primary?: boolean;
  relationship_id?: number;
  entity_name?: string;
  entity_description?: string;
  entity_thumbnail?: string | null;
  category?: string;
  mapLayerId?: string;
  media_type?: string;
  /** A related media item's original file; null for an embed. */
  url?: string | null;
}

/** An item's relationships: related rows grouped by table slug, plus media. */
export interface RelationshipsResponse {
  byTableSlug: Record<string, RelatedEntityItem[]>;
  media: RelatedEntityItem[];
}

export interface ICmTableDataRelation {
  id: number;
  name: string;
  entity_description: string;
  rel_description: string;
  relationship_id: number;
  is_primary: boolean;
  primary_image: string | null;
  thumbnail: string | null;
  table_slug?: string;
  table_name?: string;
}

export interface ICmTableDataMediaItem extends MediaAssetFields {
  id: number;
  name: string;
  entity_description: string;
  rel_description: string;
  relationship_id: number;
  is_primary: boolean;
  media_type: string;
  /** The original file; null for an embed. */
  url: string | null;
  /** Null when none is ever made (zip, wav, HEIC…). */
  thumbnail: string | null;
}

export interface INormalisedRelations {
  /** Related rows grouped by their CmTable.slug. Empty object if none. */
  byTableSlug: Record<string, ICmTableDataRelation[]>;
  /** Media attached to this row. */
  media: ICmTableDataMediaItem[];
}

// ─── Relationship Editor Types ────────────────────────────────────────────────

/** Union of the two item shapes that can appear in the editor state. */
export type RelationshipEditorItem = ICmTableDataRelation | ICmTableDataMediaItem;

/**
 * Working copy of relationships for a single anchor.
 * Keys are table slugs (e.g. "culturalsites") or the literal string "media".
 * Values are the current desired items for that group.
 */
export type RelationshipEditorState = Record<string, RelationshipEditorItem[]>;

/** Converts INormalisedRelations into a RelationshipEditorState. */
export function initRelationshipEditorState(relationships: INormalisedRelations): RelationshipEditorState {
  const state: RelationshipEditorState = {};
  for (const [slug, items] of Object.entries(relationships?.byTableSlug ?? {})) {
    state[slug] = [...items];
  }
  const media = relationships?.media ?? [];
  if (media.length > 0) {
    state["media"] = [...media];
  }
  return state;
}

export interface MediaResponse extends MediaAssetFields {
  id: number;
  name: string;
  media_type: "photo" | "video" | "pdf" | string;
  url: string;
  thumbnail: string;
  description: string;
  user: number;
  organisation: number;
  created_at: string;
  last_updated_at: string;
  original_file: {
    filename: string;
    size: string;
    type: string;
  };
  source_folder: string | null;
  relationships?: RelationshipsResponse;
}

export interface MediaItemDetailed extends MediaAssetFields {
  id: string | number;
  name: string;
  description: string;
  type: string;
  media_type?: string;
  relationships?: RelationshipsResponse;
  relations?: INormalisedRelations;

  // File information (for upload)
  file?: File;
  fileName?: string;
  /** Upload editor only: a local blob of the file being prepared. */
  previewUrl?: string;

  // URLs (for display)
  url?: string;
  thumbnail?: string;

  // File metadata
  original_file: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };

  /** Upload editor only: a pasted embed's iframe src. API media carries it as `display_url`. */
  embed_url?: string;

  // EXIF metadata extracted from the file at upload time
  exif_metadata?: Record<string, any> | null;
  // Capture location — GeoJSON Point object when set from EXIF, WKT string when returned by API
  geometry?: { type: "Point"; coordinates: [number, number] } | string | null;

  // Allow dynamic string indexing
  [key: string]: any;
}

// Base minimal fields that are always returned from API
export interface MinimalDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description?: string;
  thumbnail?: string;
}

// For use in contexts/components that only need minimal data
export interface OptionsData {
  [key: string]: MinimalDataItem[];
}

export interface UploadBatch {
  id: number;
  uuid: string; // Backend returns this but we don't use it for requests
  file_count: number;
  completed_count: number;
  total_size: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  items: Array<{
    id: number;
    name: string;
    status: string;
  }>;
}

export interface OptionsContextType {
  options: OptionsData | null;
  refreshOptions: () => Promise<void>;
  loadOptionsForEndpoint: (endpoints: string | string[]) => Promise<void>;
  isLoading: boolean;
}

// Add this with the other interfaces
export interface UploadError {
  file: string;
  stage: "pre-upload" | "upload" | "post-upload";
  error: string;
}

// Update the existing UploadPhase type
export type UploadPhase = "idle" | "importing" | "uploading" | "complete" | "error";

// ============================================================================
// API PAGINATION TYPES
// ============================================================================

// New paginated API response format
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper type for API responses that might be paginated or array
export type ApiResponse<T> = T[] | PaginatedResponse<T>;

// Helper functions for handling API responses
export function extractResultsFromResponse<T>(response: ApiResponse<T>): T[] {
  if (response && typeof response === "object" && "results" in response) {
    return response.results || [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
}

// ============================================================================
// CENTRALIZED SELECTION SYSTEM TYPES
// ============================================================================

// Define a more generic Entity type that can represent ANY entity
export interface Entity {
  id: string | number;
  name: string;
  entityType: string;
  layerId?: string;
  coordinates?: [number, number];
  geometry?: any;
  properties?: Record<string, any>;
  relatedMedia?: MediaItemDetailed[];
  hasMedia?: boolean;
  relationships?: RelationshipsResponse; // Complete relationships object from API
  isComplete?: boolean; // Flag to indicate if full data has been fetched
  // Additional fields that can be at the top level
  thumbnail?: string;
  description?: string;
  primary_image?: PrimaryImage | null;
  created_at?: string;
  last_updated_at?: string;
  url?: string; // For media items
  relations?: INormalisedRelations;
}

// ============================================================================

// ─── Spatial Dataset Warehouse ───────────────────────────────────────────────

export type SpatialDatasetSourceFormat = "geotiff" | "shapefile" | "gpx" | "geojson" | "kml" | "csv";
export type SpatialDatasetType = "raster" | "vector" | "tabular";
export type SpatialDatasetDisplayType = "cog" | "pmtiles" | "parquet";
export type SpatialDatasetProcessingStatus = "pending" | "uploading" | "queued" | "processing" | "completed" | "failed";

// ─── Asset shape (shared by Media and SpatialDataset) ─────────────────────────

/** How an asset's `display_url` is shown: a spatial output, or a media rendition. */
export type AssetDisplayType = SpatialDatasetDisplayType | "image" | "video" | "hls" | "audio" | "pdf" | "embed";

/** What a media item is (`media_kind`). Not the Field forms' `MediaKind`, which is photo/video/audio. */
export type ApiMediaKind = "image" | "video" | "audio" | "pdf" | "embed" | "other";

/** Media only ever uses pending, uploading, completed and failed. */
export type AssetProcessingStatus = SpatialDatasetProcessingStatus;

export interface AssetUploadDetails {
  status: string;
  file_key?: string;
  /** Only on create with `?return_signed_url=true`. */
  signed_url?: string;
  headers?: Record<string, string>;
  upload_method?: string;
  batch_id?: number | null;
  batch_uuid?: string | null;
}

/**
 * The fields Media and SpatialDataset share: the original upload, what to show
 * and how, and where processing is. They differ only in which `display_type`s exist.
 */
export interface AssetFields {
  /** Storage key of the original upload, not a URL. */
  original_file_key: string | null;
  original_file: { filename: string; type?: string; size?: string; hash?: string } | null;
  /** What to show, rendered as `display_type`. Null while there is nothing to show. */
  display_url: string | null;
  display_type: AssetDisplayType | null;
  /** Small preview, or null when there is none (yet, or ever). */
  thumbnail: string | null;
  processing_status: AssetProcessingStatus;
  processing_error: string;
  processing_details: Record<string, any> | null;
  upload_details: AssetUploadDetails | null;
}

/**
 * The asset fields as media carries them. All optional: external-data media has
 * none, and related items carry only a summary.
 */
export interface MediaAssetFields extends Partial<AssetFields> {
  media_kind?: ApiMediaKind;
}

export interface SpatialDatasetAttributeSchema {
  name: string;
  type: "string" | "number" | "boolean";
  /** Up to 10 distinct values in file order, as a preview. Always present. */
  sample_values: any[];
  /**
   * The COMPLETE distinct value list, sorted, capped at 500 (inclusive — a column
   * with exactly 500 is kept in full, 501 is abandoned). Present ONLY when the
   * worker captured the whole set. Absent means the set is unknown — the column
   * passed the ceiling, or the dataset predates this field — so fall back to
   * `sample_values` and treat it as a preview, never as the full set.
   */
  values?: any[];
  /** Exact number of distinct non-null values. Only ever accompanies `values`. */
  distinct_count?: number;
  /**
   * The column reads as a key rather than a category (`osm_id`, a free-text name),
   * so styling by it means roughly one rule per feature.
   *
   * INDEPENDENT of `values`: a column can be fully enumerated and still be flagged.
   * Requires ≥50 distinct values AND ≥50 features AND distinct/features > 0.5, so a
   * column with fewer than 50 distinct values is never flagged however the ratio
   * reads — it cannot produce an unreasonable style. Absent on datasets processed
   * before the worker emitted it.
   */
  high_cardinality?: boolean;
}

export interface SpatialDataset extends AssetFields {
  id: number;
  uuid: string;
  name: string;
  description: string;
  source_format: SpatialDatasetSourceFormat;
  dataset_type: SpatialDatasetType;
  organisation: number;
  user: number;
  /** S3 key path — now includes /raw/ segment: org_1/spatial/<uuid>/raw/<filename> */
  original_file_key: string;
  original_file: { filename: string } | null;
  display_url: string | null;
  display_type: SpatialDatasetDisplayType | null;
  raster_output: "cog" | "pmtiles" | null;
  processing_status: SpatialDatasetProcessingStatus;
  processing_error: string;
  processing_details: Record<string, any> | null;
  /** Always present on create. Gains signed_url + headers when ?return_signed_url=true */
  upload_details: {
    status: string;
    file_key: string;
    signed_url?: string;
    headers?: Record<string, string>;
  } | null;
  geometry: GeoJSON.Geometry | null;
  /** [minLng, minLat, maxLng, maxLat] */
  bbox: [number, number, number, number] | null;
  /** Backend sends bare digits, e.g. "4326", not "EPSG:4326" — see `buildSpatialRefSysXml`. */
  source_epsg: string | null;
  bands: { name: string; data_type: string; nodata: number | null }[] | null;
  overviews: number[] | null;
  gsd: number | null;
  attribute_schema: SpatialDatasetAttributeSchema[] | null;
  stac_properties: Record<string, any> | null;
  /** GeoStyler Style for vectors; current `raster-*` rendering properties for rasters. */
  display_style: Record<string, any>;
  /**
   * CDN URL of the analysis artifact (COG for rasters, GeoParquet for vectors,
   * Parquet for tabular) — the file QGIS/GDAL/Python open in place over HTTP.
   * Opens with the session's login CloudFront cookies. Null until conversion
   * produces one; see `analysis_type`.
   */
  analysis_url?: string | null;
  /** Which analysis artifact exists, if any. Null means "not generated yet". */
  analysis_type?: "cog" | "geoparquet" | "parquet" | null;
  /** Worker-generated thumbnail URL; null until worker sets it (SDW-15) */
  thumbnail: string | null;
  /** Geometry sub-type — set by worker once SDW-19 lands (point|line|polygon) */
  geometry_type?: "point" | "line" | "polygon" | null;
  acquisition_date: string | null;
  time_series_group_id: string | null;
  restrictions: number[];
  created_at: string;
  last_updated_at: string;
  relationships?: { media: any[] };
}

export interface SpatialDatasetCreatePayload {
  name: string;
  source_format: SpatialDatasetSourceFormat;
  dataset_type?: SpatialDatasetType;
  description?: string;
  original_file?: {
    filename: string;
    size: number;
    type: string;
  };
  acquisition_date?: string;
  time_series_group_id?: string;
  display_style?: Record<string, any>;
  restrictions?: number[];
  /** UploadBatch id or UUID — backend auto-increments completed_count on multipart/complete */
  upload_batch?: string | number;
  /**
   * For GeoTIFF multipart uploads: set at create time so multipart/complete auto-triggers
   * the correct output (§7b). For single-PUT GeoTIFF the /process/ call handles this.
   * Ignored by the backend for vector formats.
   */
  raster_output?: "cog" | "pmtiles";
}

export interface SpatialDatasetUpdatePayload {
  name?: string;
  description?: string;
  display_style?: Record<string, any>;
  acquisition_date?: string | null;
  time_series_group_id?: string | null;
  restrictions?: number[];
}
